import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Alert, AlertDescription } from '@/components/ui/alert';

export const Register = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState('guest');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    const result = await register(email, password, name, role);
    setLoading(false);
    if (result.success) navigate(role === 'host' ? '/list-property' : '/');
    else setError(result.error);
  };

  const inputCls = 'h-11 rounded-md border bg-white focus-visible:ring-2 focus-visible:ring-[color:var(--sea)] focus-visible:ring-offset-0';

  return (
    <div className="min-h-screen flex items-center justify-center bg-paper py-16 px-6">
      <div className="w-full max-w-md">
        <div className="text-center mb-9">
          <Link to="/" className="inline-flex items-baseline gap-2 mb-6">
            <span className="font-display font-bold text-[19px] tracking-[-0.03em]">Cape&nbsp;Town</span>
            <span className="font-display-italic text-[21px] leading-none">Monthly</span>
          </Link>
          <h1 className="font-display leading-[1.06] tracking-[-0.03em] text-ink" style={{ fontSize: 'clamp(32px, 4vw, 44px)' }} data-testid="register-title">
            Create an <span className="font-display-italic">account</span>
          </h1>
          <p className="mt-2 text-[15px] text-muted">Two minutes, tops</p>
        </div>

        <div className="bg-white border rounded-[12px] p-8 shadow-[0_1px_2px_rgba(0,0,0,0.03)]" style={{ borderColor: 'var(--line-2)' }}>
          <form onSubmit={handleSubmit} className="space-y-5">
            {error && (
              <Alert variant="destructive" data-testid="register-error-alert">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <div className="space-y-2">
              <Label htmlFor="name" className="text-[13px] font-semibold text-ink-2">Full name</Label>
              <Input id="name" type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="Jane Doe" required data-testid="register-name-input" className={inputCls} style={{ borderColor: 'var(--line-2)' }} />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email" className="text-[13px] font-semibold text-ink-2">Email</Label>
              <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" required data-testid="register-email-input" className={inputCls} style={{ borderColor: 'var(--line-2)' }} />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-[13px] font-semibold text-ink-2">Password</Label>
              <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" required data-testid="register-password-input" className={inputCls} style={{ borderColor: 'var(--line-2)' }} />
            </div>

            <div className="space-y-3">
              <Label className="text-[13px] font-semibold text-ink-2">I want to</Label>
              <RadioGroup value={role} onValueChange={setRole} className="grid grid-cols-2 gap-3" data-testid="register-role-selection">
                <label htmlFor="guest" className={`cursor-pointer rounded-md border p-3 flex items-center gap-2 transition-colors ${role === 'guest' ? 'border-sea bg-sea-soft' : ''}`} style={{ borderColor: role === 'guest' ? 'var(--sea)' : 'var(--line-2)' }}>
                  <RadioGroupItem value="guest" id="guest" data-testid="register-role-guest" />
                  <span className="text-[14px] font-medium">Find a place</span>
                </label>
                <label htmlFor="host" className={`cursor-pointer rounded-md border p-3 flex items-center gap-2 transition-colors ${role === 'host' ? 'border-sea bg-sea-soft' : ''}`} style={{ borderColor: role === 'host' ? 'var(--sea)' : 'var(--line-2)' }}>
                  <RadioGroupItem value="host" id="host" data-testid="register-role-host" />
                  <span className="text-[14px] font-medium">List my place</span>
                </label>
              </RadioGroup>
            </div>

            <button type="submit" disabled={loading} className="btn btn-sea btn-lg w-full disabled:opacity-60" data-testid="register-submit-btn">
              {loading ? 'Creating…' : 'Create account'}
            </button>
          </form>

          <p className="mt-6 text-center text-[14px] text-muted">
            Already have an account?{' '}
            <Link to="/login" className="text-ink font-semibold hover:text-sea transition-colors" data-testid="register-login-link">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};
